import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import images from "../../utilities/images/images";
import {
  createPracticeSet,
  fetchPracticeSets,
  fetchUserWrongTopics,
} from "./mistakeBookApi";
import { getMistakeBookSubjectsForCourse } from "./mistakeBookSubjects";
import PracticeSetModeModal from "./PracticeSetModeModal";
import {
  setSelectedSetId,
  setTestDetails,
  setTestType,
  setPaperId,
} from "../../redux/exam/examSlice";
import boltIcon from "./assets/ps-bolt.svg";
import analyticsIcon from "./assets/ps-analytics.svg";
import plusIcon from "./assets/ps-plus.svg";
import closeIcon from "./assets/ps-create-close.svg";
import {
  MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH,
  MISTAKE_BOOK_PRACTICE_SETS_PATH,
} from "./mistakeBookRoutes";
import "./PracticeSetsPage.css";

const MISTAKE_BOOK_PAPER_TYPE = 22;
const QUESTION_COUNTS = [10, 15, 25];
const ICON_TONES = ["#00bba7", "#9810fa", "#2563eb", "#ea580c"];
const INSUFFICIENT_QUESTIONS_MESSAGE =
  "Question Count should be more than 10";

const formatSetDate = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/[a-z]/i.test(raw) && raw.includes(",")) return raw;

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const countAvailableQuestions = (topics = []) =>
  topics.reduce((sum, topic) => sum + (Number(topic.mistakes) || 0), 0);

const PracticeSetsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const state = location.state || {};
  const userPreference = useSelector((store) => store.userPreference);
  const targetId = userPreference?.targetDetails?.[0]?.target_id;
  const targetName = userPreference?.targetDetails?.[0]?.target ?? "";

  const subjects = useMemo(
    () => getMistakeBookSubjectsForCourse({ targetId, targetName }),
    [targetId, targetName]
  );

  const initialSubjectName =
    state.subjectName || state.subject?.name || subjects[0]?.name || "Physics";
  const initialSubjectId =
    state.subjectId ||
    state.subject?.id ||
    subjects.find((s) => s.name === initialSubjectName)?.id ||
    subjects[0]?.id ||
    "physics";

  const year = state.year;

  const [activeTab, setActiveTab] = useState(
    state.practiceSetsTab === "completed" ? "completed" : "active"
  );
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [error, setError] = useState("");
  const [sets, setSets] = useState([]);
  const [remainingQuestions, setRemainingQuestions] = useState(
    Number.isFinite(Number(state.remainingQuestions))
      ? Number(state.remainingQuestions)
      : null
  );
  const [listSubjectName, setListSubjectName] = useState(initialSubjectName);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [chooserSubject, setChooserSubject] = useState(initialSubjectName);
  const [questionCount, setQuestionCount] = useState(10);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [modeModalOpen, setModeModalOpen] = useState(false);
  const [selectedSet, setSelectedSet] = useState(null);

  // Keep subject when returning from analytics / practice-summary.
  useEffect(() => {
    const nextSubject = state.subjectName || state.subject?.name || "";
    if (nextSubject && nextSubject !== listSubjectName) {
      setListSubjectName(nextSubject);
      setChooserSubject(nextSubject);
    }
    if (state.practiceSetsTab === "completed" && activeTab !== "completed") {
      setActiveTab("completed");
    }
  }, [state.subjectName, state.subject?.name, state.practiceSetsTab]);

  const loadPracticeSets = useCallback(async (subject = listSubjectName) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchPracticeSets({ subject });
      setSets(payload.sets || []);
      if (payload.remainingQuestions >= 0) {
        setRemainingQuestions(payload.remainingQuestions);
      }
    } catch (err) {
      setSets([]);
      setError(
        err?.response?.data?.message ||
          "Unable to load practice sets right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [listSubjectName]);

  useEffect(() => {
    loadPracticeSets(listSubjectName);
  }, [loadPracticeSets, listSubjectName]);

  const visibleSets = useMemo(() => {
    if (activeTab === "completed") {
      return sets.filter((set) => set.isCompleted);
    }
    return sets.filter((set) => !set.isCompleted);
  }, [sets, activeTab]);

  const handleBack = () => {
    history.push("/practice/mistake-book", {
      year,
      subjectId: initialSubjectId,
    });
  };

  const showInsufficientPopup = (message = INSUFFICIENT_QUESTIONS_MESSAGE) => {
    setChooserOpen(false);
    setPopupMessage(message);
    setPopupOpen(true);
  };

  const resolveAvailableQuestions = async (subject) => {
    if (
      remainingQuestions != null &&
      remainingQuestions >= 0 &&
      subject === listSubjectName
    ) {
      return remainingQuestions;
    }

    try {
      const payload = await fetchUserWrongTopics({ subject });
      const available = countAvailableQuestions(payload.topics || []);
      if (subject === listSubjectName) {
        setRemainingQuestions(available);
      }
      return available;
    } catch {
      return remainingQuestions != null && subject === listSubjectName
        ? remainingQuestions
        : null;
    }
  };

  const handleCreateClick = async () => {
    if (creating || checkingAvailability || loading) return;

    setCheckingAvailability(true);
    try {
      const available = await resolveAvailableQuestions(listSubjectName);
      if (available != null && available < 10) {
        showInsufficientPopup();
        return;
      }

      setChooserSubject(listSubjectName);
      setQuestionCount(10);
      setChooserOpen(true);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleCreatePracticeSet = async () => {
    if (creating) return;
    setCreating(true);
    setError("");
    try {
      const available = await resolveAvailableQuestions(chooserSubject);
      if (available != null && available < 10) {
        showInsufficientPopup();
        return;
      }

      const result = await createPracticeSet({
        subject: chooserSubject,
        question_count: questionCount,
      });
      setChooserOpen(false);

      const nextRemaining = result?.data?.remainingAfterThisSet;
      if (Number.isFinite(nextRemaining)) {
        setRemainingQuestions(nextRemaining);
      }

      if (chooserSubject !== listSubjectName) {
        setListSubjectName(chooserSubject);
      } else {
        await loadPracticeSets(chooserSubject);
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not create practice set. Please try again.";

      const lower = String(message).toLowerCase();
      if (
        lower.includes("question_count") ||
        lower.includes("subject is required") ||
        lower.includes("not enough") ||
        lower.includes("insufficient") ||
        lower.includes("less than")
      ) {
        showInsufficientPopup();
      } else {
        setChooserOpen(false);
        setPopupMessage(message);
        setPopupOpen(true);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleStartOrResume = (set) => {
    const setId = set?.setId ?? set?.id;
    if (setId == null || setId === "") return;
    setSelectedSet(set);
    setModeModalOpen(true);
  };

  const handleAnalyticsClick = (set) => {
    if (!set?.isCompleted) return;
    const setId = set?.setId ?? set?.id;
    if (setId == null || setId === "") return;

    const subjectName = listSubjectName;
    const subjectId =
      subjects.find((s) => s.name === subjectName)?.id || initialSubjectId;

    const practiceSetsReturnState = {
      practiceSetsTab: "completed",
      fromMistakeBookPracticeSet: true,
      subjectName,
      subjectId,
      subject: { id: subjectId, name: subjectName },
      year,
    };

    const payload = {
      paperType: MISTAKE_BOOK_PAPER_TYPE,
      setId,
      practiceMode: "test",
      gamificationOn: false,
      source: "mistake_book_practice_set",
      test_name: set.title || `Set ${set.setNumber || setId}`,
      subject: subjectName,
      subjectId,
      returnTo: MISTAKE_BOOK_PRACTICE_SETS_PATH,
    };

    dispatch(setSelectedSetId(setId));
    dispatch(setTestType({ id: MISTAKE_BOOK_PAPER_TYPE }));
    dispatch(setPaperId(null));
    dispatch(setTestDetails(payload));

    history.push(MISTAKE_BOOK_PRACTICE_SETS_ANALYTICS_PATH, {
      paper_type: MISTAKE_BOOK_PAPER_TYPE,
      set_id: setId,
      returnTo: MISTAKE_BOOK_PRACTICE_SETS_PATH,
      ...practiceSetsReturnState,
    });
  };

  const handleModeConfirm = ({ selectedMode, gamificationOn }) => {
    if (!selectedSet) return;
    const setId = selectedSet.setId ?? selectedSet.id;

    setModeModalOpen(false);
    history.push("/practice/mistake-book/practice-set-instruction", {
      practiceSet: selectedSet,
      set_id: setId,
      selectedMode,
      gamificationOn:
        selectedMode === "practice" ? Boolean(gamificationOn) : false,
      subject: { id: initialSubjectId, name: listSubjectName },
      subjectId: initialSubjectId,
      subjectName: listSubjectName,
      year,
    });
    setSelectedSet(null);
  };

  const modeModalTitle = selectedSet
    ? `${selectedSet.title || `Set - ${selectedSet.setNumber || selectedSet.id}`} (${selectedSet.questionCount || 0} Questions)`
    : "Practice Set";

  return (
    <div className="practice-sets-page">
      <header className="practice-sets-header">
        <button
          type="button"
          className="practice-sets-header__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={images.new_backarrow} alt="" />
        </button>
        <h1 className="practice-sets-header__title">Practice Sets</h1>
        <div className="practice-sets-header__spacer" aria-hidden="true" />
      </header>

      <nav className="practice-sets-tabs" aria-label="Practice set filters">
        <button
          type="button"
          className={`practice-sets-tab${
            activeTab === "active" ? " is-active" : ""
          }`}
          onClick={() => setActiveTab("active")}
        >
          Practice Sets
        </button>
        <button
          type="button"
          className={`practice-sets-tab${
            activeTab === "completed" ? " is-active" : ""
          }`}
          onClick={() => setActiveTab("completed")}
        >
          Completed
        </button>
      </nav>

      <div className="practice-sets-body">
        {loading ? (
          <div className="practice-sets-loader">
            <ClipLoader size={40} color="#C61C26" />
          </div>
        ) : (
          <>
            {error ? <p className="practice-sets-error">{error}</p> : null}

            {visibleSets.length ? (
              <div className="practice-sets-grid">
                {visibleSets.map((set, index) => {
                  const tone = ICON_TONES[index % ICON_TONES.length];
                  const actionLabel = set.isCompleted
                    ? "Reattempt"
                    : set.isResume
                      ? "Resume"
                      : "Start Test";
                  const dateLabel = formatSetDate(set.dateLabel);
                  return (
                    <article key={set.id} className="practice-set-card">
                      <div className="practice-set-card__top">
                        <div className="practice-set-card__main">
                          <div
                            className="practice-set-card__icon"
                            style={{ background: tone }}
                          >
                            <img src={boltIcon} alt="" />
                          </div>
                          <div className="practice-set-card__text">
                            <h2>{set.title}</h2>
                            <p className="practice-set-card__questions">
                              {set.questionCount} Questions
                            </p>
                            <p className="practice-set-card__meta">
                              <span>{set.subject || listSubjectName}</span>
                              {dateLabel ? (
                                <>
                                  <span className="practice-set-card__divider">
                                    |
                                  </span>
                                  <span>{dateLabel}</span>
                                </>
                              ) : null}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`practice-set-card__analytics${
                            set.isCompleted ? "" : " is-disabled"
                          }`}
                          aria-label="View summary"
                          disabled={!set.isCompleted}
                          onClick={() => handleAnalyticsClick(set)}
                        >
                          <img src={analyticsIcon} alt="" />
                        </button>
                      </div>
                      <button
                        type="button"
                        className={`practice-set-card__action${
                          set.isCompleted || set.isResume
                            ? " practice-set-card__action--outlined"
                            : ""
                        }`}
                        onClick={() => handleStartOrResume(set)}
                      >
                        {actionLabel}
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="practice-sets-empty">
                {activeTab === "completed"
                  ? "No completed practice sets yet."
                  : "No practice sets yet. Create one from your mistakes."}
              </p>
            )}
          </>
        )}
      </div>

      <div className="practice-sets-cta">
        <div className="practice-sets-cta__inner">
          <div className="practice-sets-cta__copy">
            <h2>Build Practice Sets from your mistakes</h2>
            <p>
              Auto-mix questions across selected topics to drill what matters
              most.
            </p>
          </div>
          <button
            type="button"
            className="practice-sets-cta__button"
            onClick={handleCreateClick}
            disabled={creating || checkingAvailability || loading}
          >
            <img src={plusIcon} alt="" />
            <span>
              {checkingAvailability ? "Checking..." : "Create practice set"}
            </span>
          </button>
        </div>
      </div>

      {chooserOpen ? (
        <div
          className="practice-sets-popup-overlay"
          role="presentation"
          onClick={() => !creating && setChooserOpen(false)}
        >
          <div
            className="ps-create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ps-create-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="ps-create-modal__close"
              onClick={() => !creating && setChooserOpen(false)}
              disabled={creating}
              aria-label="Close"
            >
              <img src={closeIcon} alt="" />
            </button>

            <div className="ps-create-modal__body">
              <div className="ps-create-modal__header">
                <h3 id="ps-create-modal-title">Create Practice Set</h3>
                <p>Choose subject and question count</p>
              </div>

              <div className="ps-create-modal__section">
                <p className="ps-create-modal__label">Subject</p>
                <div className="ps-create-modal__subjects" role="group">
                  {subjects.map((subject) => {
                    const isActive = chooserSubject === subject.name;
                    return (
                      <button
                        key={subject.id}
                        type="button"
                        className={`ps-create-modal__chip${
                          isActive ? " is-active" : ""
                        }`}
                        onClick={() => setChooserSubject(subject.name)}
                        disabled={creating}
                      >
                        {subject.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="ps-create-modal__section">
                <p className="ps-create-modal__label">Number of Questions</p>
                <div className="ps-create-modal__counts" role="group">
                  {QUESTION_COUNTS.map((count) => {
                    const isActive = questionCount === count;
                    return (
                      <button
                        key={count}
                        type="button"
                        className={`ps-create-modal__count${
                          isActive ? " is-active" : ""
                        }`}
                        onClick={() => setQuestionCount(count)}
                        disabled={creating}
                      >
                        <span className="ps-create-modal__count-value">
                          {count}
                        </span>
                        <span className="ps-create-modal__count-label">
                          Questions
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="ps-create-modal__submit"
              onClick={handleCreatePracticeSet}
              disabled={creating}
            >
              {creating ? "Creating..." : "Create Set"}
            </button>
          </div>
        </div>
      ) : null}

      {popupOpen ? (
        <div
          className="practice-sets-popup-overlay"
          role="presentation"
          onClick={() => setPopupOpen(false)}
        >
          <div
            className="practice-sets-popup"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="practice-sets-error-title"
            aria-describedby="practice-sets-error-message"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="practice-sets-error-title">Unable to build practice set</h3>
            <p id="practice-sets-error-message">{popupMessage}</p>
            <button
              type="button"
              className="practice-sets-popup__btn"
              onClick={() => setPopupOpen(false)}
            >
              Okay
            </button>
          </div>
        </div>
      ) : null}

      <PracticeSetModeModal
        key={selectedSet?.id || "mode-modal"}
        isOpen={modeModalOpen}
        setTitle={modeModalTitle}
        onClose={() => {
          setModeModalOpen(false);
          setSelectedSet(null);
        }}
        onConfirm={handleModeConfirm}
      />
    </div>
  );
};

export default PracticeSetsPage;
